import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  ArrowRight, 
  CheckCircle2,
  Users,
  Heart,
  Zap,
  GraduationCap
} from 'lucide-react';

const Careers = () => {
  const benefits = [
    { icon: Heart, title: 'Health Insurance', description: 'Comprehensive health coverage for you and your family' },
    { icon: Zap, title: 'Professional Growth', description: 'Continuous learning and career development opportunities' },
    { icon: Users, title: 'Collaborative Culture', description: 'Work with talented professionals in a supportive environment' },
    { icon: GraduationCap, title: 'Training & Certifications', description: 'Access to industry certifications and training programs' },
  ];

  const openings = [
    {
      title: 'Senior SAP Consultant',
      department: 'ERP Solutions',
      location: 'Dubai, UAE',
      type: 'Full-time',
      experience: '5+ years'
    },
    {
      title: 'Full Stack Developer',
      department: 'IT Development',
      location: 'India (Remote)',
      type: 'Full-time',
      experience: '3+ years'
    },
    {
      title: 'Oracle ERP Specialist',
      department: 'ERP Solutions',
      location: 'Dubai, UAE',
      type: 'Full-time',
      experience: '4+ years'
    },
    {
      title: 'IT Project Manager',
      department: 'Consulting',
      location: 'Dubai, UAE',
      type: 'Full-time',
      experience: '6+ years'
    },
    {
      title: 'Salesforce Developer',
      department: 'ERP Solutions',
      location: 'India (Remote)',
      type: 'Full-time',
      experience: '3+ years'
    },
    {
      title: 'Business Analyst',
      department: 'Consulting',
      location: 'Dubai, UAE',
      type: 'Full-time',
      experience: '3+ years'
    }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 gradient-hero">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">Careers</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Join Our Team
            </h1>
            <p className="text-lg text-white/80">
              Be part of a team that's transforming enterprises through technology excellence. 
              We're always looking for talented individuals who share our passion for innovation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Why Work at TeqDeepSeek?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We offer a dynamic work environment where you can grow professionally 
              while making a real impact on enterprise technology.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center text-accent mx-auto mb-4">
                  <benefit.icon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-bold text-primary mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="section bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Open Positions
            </h2>
            <p className="text-slate-600">
              Find your next opportunity and join our growing team.
            </p>
          </motion.div>

          <div className="space-y-4">
            {openings.map((job, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-1">{job.title}</h3>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {job.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {job.type}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full">
                      {job.experience}
                    </span>
                    <button className="btn-primary btn-sm">
                      Apply Now
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Don't See the Right Fit?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              We're always interested in meeting talented people. Send us your resume 
              and we'll keep you in mind for future opportunities.
            </p>
            <Link to="/contact" className="btn bg-white text-accent hover:bg-slate-100 btn-lg">
              Send Your Resume
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
